import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Check, MapPin, Save } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { toast } from 'sonner';
import { useUserProfile } from '@/lib/useUserProfile';
import { NavBar } from '@/components/NavBar';

const COUNTRIES = [
  { code: 'CN', name: 'China' }, { code: 'IN', name: 'India' },
  { code: 'ID', name: 'Indonesia' }, { code: 'VN', name: 'Vietnam' },
  { code: 'PH', name: 'Philippines' }, { code: 'TH', name: 'Thailand' },
  { code: 'MY', name: 'Malaysia' }, { code: 'SG', name: 'Singapore' },
  { code: 'JP', name: 'Japan' }, { code: 'KR', name: 'South Korea' },
  { code: 'TW', name: 'Taiwan' }, { code: 'MM', name: 'Myanmar' },
  { code: 'KH', name: 'Cambodia' }, { code: 'LA', name: 'Laos' },
  { code: 'BD', name: 'Bangladesh' }, { code: 'NZ', name: 'New Zealand' },
  { code: 'PG', name: 'Papua New Guinea' },
];

const RISK_CATEGORIES = [
  { id: 'geopolitical', label: 'Geopolitical instability', icon: '🌍' },
  { id: 'trade_policy', label: 'Trade policy and tariffs', icon: '📊' },
  { id: 'freight_logistics', label: 'Freight and logistics disruption', icon: '🚢' },
  { id: 'regulatory', label: 'Regulatory and compliance changes', icon: '📋' },
  { id: 'labour_market', label: 'Labour market and workforce', icon: '👷' },
  { id: 'climate_disaster', label: 'Natural disasters and climate', icon: '🌪️' },
  { id: 'currency_financial', label: 'Currency and financial risk', icon: '💱' },
  { id: 'supplier_concentration', label: 'Supplier concentration risk', icon: '🏭' },
];

const SettingsPage = () => {
  const navigate = useNavigate();
  const { profile, setProfile } = useUserProfile();

  const [form, setForm] = useState({
    company_name: profile?.company_name || '',
    audience_type: profile?.audience_type || '',
    industry_sector: profile?.industry_sector || '',
    company_size: profile?.company_size || '',
    primary_role: profile?.primary_role || '',
    monitored_countries: profile?.monitored_countries || [],
    risk_categories: profile?.risk_categories || RISK_CATEGORIES.map(c => c.id),
    // Preserve audience-specific fields
    import_categories: profile?.import_categories || [],
    annual_import_value: profile?.annual_import_value || '',
    gov_level: profile?.gov_level || '',
    gov_goods: profile?.gov_goods || [],
    has_apac_panels: profile?.has_apac_panels || '',
    defence_role: profile?.defence_role || '',
    capability_domains: profile?.capability_domains || [],
    aukus_subject: profile?.aukus_subject || '',
  });

  const toggleCountry = (code) => {
    setForm(prev => ({
      ...prev,
      monitored_countries: prev.monitored_countries.includes(code)
        ? prev.monitored_countries.filter(c => c !== code)
        : [...prev.monitored_countries, code]
    }));
  };

  const toggleCategory = (id) => {
    setForm(prev => ({
      ...prev,
      risk_categories: prev.risk_categories.includes(id)
        ? prev.risk_categories.filter(c => c !== id)
        : [...prev.risk_categories, id]
    }));
  };

  const handleSave = () => {
    setProfile(form);
    toast.success('Preferences saved');
    navigate('/dashboard');
  };

  return (
    <div className="min-h-screen bg-[#F5F5F5]">
      <NavBar />

      <main className="pt-24 px-4 lg:px-6 pb-8">
        <div className="max-w-[800px] mx-auto">
          <Button
            variant="ghost"
            onClick={() => navigate('/dashboard')}
            className="mt-6 text-[#4E6974] hover:text-[#0D3B4F]"
            data-testid="back-to-dashboard-btn"
          >
            <ArrowLeft className="mr-2 h-4 w-4" /> Back to dashboard
          </Button>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mt-4 mb-8">
            <h1 className="text-3xl font-bold text-[#0D3B4F]" data-testid="settings-title">
              Dashboard preferences
            </h1>
            <p className="text-[#4E6974] mt-2">
              Update your preferences to personalise your TradePath experience
            </p>
          </motion.div>

          {/* Company Profile */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
            <Card className="bg-white rounded-lg shadow-[0_4px_24px_rgba(0,0,0,0.04)] border-none mb-6">
              <CardHeader>
                <CardTitle className="text-lg font-bold text-[#0D3B4F]">Company profile</CardTitle>
              </CardHeader>
              <CardContent className="space-y-5">
                <div>
                  <Label htmlFor="company_name" className="text-[#0D3B4F]">Company name</Label>
                  <Input
                    id="company_name"
                    value={form.company_name}
                    onChange={(e) => setForm({ ...form, company_name: e.target.value })}
                    className="mt-1.5"
                    placeholder="Enter your company name"
                    data-testid="settings-company-name"
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <Label className="text-[#0D3B4F]">Industry sector</Label>
                    <Select value={form.industry_sector} onValueChange={(v) => setForm({ ...form, industry_sector: v })}>
                      <SelectTrigger className="mt-1.5" data-testid="settings-industry">
                        <SelectValue placeholder="Select industry" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="agribusiness">Agribusiness</SelectItem>
                        <SelectItem value="mining">Mining and resources</SelectItem>
                        <SelectItem value="defence">Defence</SelectItem>
                        <SelectItem value="government">Government</SelectItem>
                        <SelectItem value="manufacturing">Manufacturing</SelectItem>
                        <SelectItem value="logistics">Logistics</SelectItem>
                        <SelectItem value="other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-[#0D3B4F]">Company size</Label>
                    <Select value={form.company_size} onValueChange={(v) => setForm({ ...form, company_size: v })}>
                      <SelectTrigger className="mt-1.5" data-testid="settings-size">
                        <SelectValue placeholder="Select size" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="under-20m">Under $20M</SelectItem>
                        <SelectItem value="20m-100m">$20M - $100M</SelectItem>
                        <SelectItem value="100m-500m">$100M - $500M</SelectItem>
                        <SelectItem value="500m-plus">$500M+</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-[#0D3B4F]">Primary role</Label>
                    <Select value={form.primary_role} onValueChange={(v) => setForm({ ...form, primary_role: v })}>
                      <SelectTrigger className="mt-1.5" data-testid="settings-role">
                        <SelectValue placeholder="Select role" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="supply-chain">Supply chain</SelectItem>
                        <SelectItem value="procurement">Procurement</SelectItem>
                        <SelectItem value="risk">Risk</SelectItem>
                        <SelectItem value="executive">Executive</SelectItem>
                        <SelectItem value="operations">Operations</SelectItem>
                        <SelectItem value="other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>

          {/* Countries */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
            <Card className="bg-white rounded-lg shadow-[0_4px_24px_rgba(0,0,0,0.04)] border-none mb-6">
              <CardHeader>
                <CardTitle className="text-lg font-bold text-[#0D3B4F]">Countries of interest</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
                  {COUNTRIES.map((c) => (
                    <button
                      key={c.code}
                      onClick={() => toggleCountry(c.code)}
                      className={`p-2.5 rounded-lg border-2 transition-all flex items-center gap-2 text-left text-sm ${
                        form.monitored_countries.includes(c.code)
                          ? 'border-[#0097A7] bg-[#0097A7]/5 text-[#0D3B4F] font-medium'
                          : 'border-[#E0E0E0] text-[#4E6974] hover:border-[#0097A7]/50'
                      }`}
                      data-testid={`settings-country-${c.code.toLowerCase()}`}
                    >
                      <MapPin className={`h-3.5 w-3.5 flex-shrink-0 ${form.monitored_countries.includes(c.code) ? 'text-[#0097A7]' : 'text-[#4E6974]'}`} />
                      {c.name}
                      {form.monitored_countries.includes(c.code) && <Check className="h-3.5 w-3.5 text-[#0097A7] ml-auto" />}
                    </button>
                  ))}
                </div>
              </CardContent>
            </Card>
          </motion.div>

          {/* Risk Categories */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}>
            <Card className="bg-white rounded-lg shadow-[0_4px_24px_rgba(0,0,0,0.04)] border-none mb-6">
              <CardHeader>
                <CardTitle className="text-lg font-bold text-[#0D3B4F]">Priority risk categories</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {RISK_CATEGORIES.map((cat) => (
                    <button
                      key={cat.id}
                      onClick={() => toggleCategory(cat.id)}
                      className={`p-3 rounded-lg border-2 transition-all flex items-center gap-3 text-left ${
                        form.risk_categories.includes(cat.id)
                          ? 'border-[#0097A7] bg-[#0097A7]/5'
                          : 'border-[#E0E0E0] hover:border-[#0097A7]/50'
                      }`}
                      data-testid={`settings-category-${cat.id}`}
                    >
                      <span className="text-xl">{cat.icon}</span>
                      <span className={`text-sm font-medium ${form.risk_categories.includes(cat.id) ? 'text-[#0D3B4F]' : 'text-[#4E6974]'}`}>
                        {cat.label}
                      </span>
                      {form.risk_categories.includes(cat.id) && <Check className="h-4 w-4 text-[#0097A7] ml-auto" />}
                    </button>
                  ))}
                </div>
              </CardContent>
            </Card>
          </motion.div>

          {/* Save */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }} className="flex justify-end mb-12">
            <Button
              onClick={handleSave}
              className="bg-[#00707C] hover:bg-[#00707C]/90 text-white px-8"
              data-testid="settings-save-btn"
            >
              <Save className="h-4 w-4 mr-2" />
              Save preferences
            </Button>
          </motion.div>
        </div>
      </main>
    </div>
  );
};

export default SettingsPage;
